<template>
  <div class="login-shell">
    <section class="login-left" aria-hidden="true">
      <div class="brand-panel">
        <div class="brand-mark">OT</div>
        <h2 class="brand-name">Obra Tycoon</h2>
        <p class="brand-tagline">Comece agora. Sua obra estará no ar em menos de 1 minuto.</p>
        <ul class="brand-features">
          <li>Um sistema exclusivo por obra</li>
          <li>Acesso via subdomínio próprio</li>
          <li>Convide sua equipe depois</li>
        </ul>
      </div>
    </section>

    <section class="login-right">
      <div class="login-content">
        <div class="login-header">
          <span class="login-product">Obra Tycoon</span>
          <h1 class="login-title">Registrar obra</h1>
          <p class="login-subtitle">Crie o administrador e o sistema da obra.</p>
        </div>

        <form class="form-grid" @submit.prevent="handleRegister">
          <div class="input-field">
            <label for="name">Nome do perfil</label>
            <input id="name" v-model="name" type="text" placeholder="Ex.: Ana Souza" autocomplete="name" />
          </div>

          <div class="input-field">
            <label for="username">Nome de usuário</label>
            <input id="username" v-model="username" type="text" placeholder="Ex.: ana.souza" autocomplete="username" />
          </div>

          <div class="input-field">
            <label for="birthDate">Data de nascimento</label>
            <input id="birthDate" v-model="birthDate" type="date" :min="MIN_BIRTH_DATE" :max="maxBirthDate()" autocomplete="bday" required />
          </div>

          <div class="input-field">
            <label for="password">Senha</label>
            <input id="password" v-model="password" type="password" placeholder="Digite uma senha" autocomplete="new-password" />
          </div>

          <div class="field-divider">
            <span>Dados da obra</span>
          </div>

          <div class="input-field">
            <label for="projectName">Nome da obra</label>
            <input id="projectName" v-model="projectName" type="text" placeholder="Ex.: Obra Centro 2026" />
          </div>

          <div class="input-field">
            <label for="systemUsername">Identificador da obra</label>
            <input id="systemUsername" v-model="systemUsername" type="text" placeholder="Ex.: obracentro" autocomplete="off" />
            <span class="input-hint">Usado no URL: <strong>obracentro</strong>.obratycoon.com.br</span>
          </div>

          <label class="consent-field">
            <input v-model="acceptedTerms" type="checkbox" />
            <span>Li e aceito o <router-link to="/termos" target="_blank">Termo de Uso</router-link> e a <router-link to="/privacidade" target="_blank">Política de Privacidade</router-link>.</span>
          </label>

          <p v-if="error" class="login-error">{{ error }}</p>

          <button class="btn-primary" type="submit" :disabled="loading || !acceptedTerms">{{ loading ? 'Criando obra...' : 'Criar obra e entrar' }}</button>

          <div class="login-links">
            <button class="link-btn" type="button" @click="goLogin">Já tenho conta</button>
          </div>
        </form>
      </div>
    </section>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref } from 'vue'
import { useRouter } from 'vue-router'
import { accountsStore } from '@/store/accounts'
import { sessionStore } from '@/store/session'
import { birthDateError, maxBirthDate, MIN_BIRTH_DATE } from '@/utils/birthDate'

export default defineComponent({
  name: 'RegisterProjectView',
  setup() {
    const router = useRouter()
    const name = ref('')
    const username = ref('')
    const password = ref('')
    const birthDate = ref('')
    const projectName = ref('')
    const systemUsername = ref('')
    const error = ref('')
    const loading = ref(false)
    const acceptedTerms = ref(false)

    const handleRegister = async () => {
      error.value = ''
      if (!name.value.trim() || !username.value.trim() || !password.value.trim() || !projectName.value.trim() || !systemUsername.value.trim()) {
        error.value = 'Preencha todos os campos.'
        return
      }
      const birthErr = birthDateError(birthDate.value)
      if (birthErr) { error.value = birthErr; return }
      if (!acceptedTerms.value) {
        error.value = 'É necessário aceitar o Termo de Uso e a Política de Privacidade.'
        return
      }
      loading.value = true
      try {
        const account = await accountsStore.registerProject(
          username.value.trim(),
          password.value.trim(),
          name.value.trim(),
          projectName.value.trim(),
          systemUsername.value.trim(),
          'gratuito',
          acceptedTerms.value,
          birthDate.value
        )
        sessionStore.login({
          name: account.name || account.username,
          role: account.role,
          username: account.username,
          avatar: account.avatar,
          uid: account.uid,
          roleId: account.roleId,
          subordinateUsernames: account.subordinateUsernames,
          hasSubordinates: account.hasSubordinates,
          isAnalyst: Boolean(account.isAnalyst)
        })
        router.push('/home')
      } catch (err: unknown) {
        error.value = err instanceof Error ? err.message : 'Falha ao criar obra.'
      } finally {
        loading.value = false
      }
    }

    const goLogin = () => router.push('/login')

    return { MIN_BIRTH_DATE, maxBirthDate, birthDate, name, username, password, projectName, systemUsername, error, loading, acceptedTerms, handleRegister, goLogin }
  }
})
</script>

<style scoped lang="scss">
.login-shell {
  min-height: 100vh;
  min-height: 100dvh;
  display: flex;
  overflow: hidden;
}

/* ── LEFT PANEL ── */
.login-left {
  flex: 0 0 42%;
  background: #0d1f35;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 48px 40px;
  position: relative;
  overflow: hidden;
}

.login-left::before {
  content: '';
  position: absolute;
  inset: 0;
  background: radial-gradient(ellipse at 20% 50%, rgba(15, 76, 129, 0.5) 0%, transparent 65%),
              radial-gradient(ellipse at 80% 20%, rgba(79, 51, 0, 0.3) 0%, transparent 55%);
  pointer-events: none;
}

.brand-panel {
  position: relative;
  z-index: 1;
  max-width: 320px;
  color: #ffffff;
}

.brand-mark {
  width: 52px;
  height: 52px;
  border-radius: 14px;
  background: #0f4c81;
  display: grid;
  place-items: center;
  font-size: 20px;
  font-weight: 800;
  letter-spacing: -0.5px;
  color: #fff;
  margin-bottom: 20px;
}

.brand-name {
  font-size: 28px;
  font-weight: 700;
  margin: 0 0 8px;
  letter-spacing: -0.3px;
}

.brand-tagline {
  font-size: 15px;
  color: rgba(255, 255, 255, 0.55);
  margin: 0 0 32px;
  line-height: 1.6;
}

.brand-features {
  list-style: none;
  padding: 0;
  margin: 0;
  display: grid;
  gap: 10px;

  li {
    font-size: 13px;
    color: rgba(255, 255, 255, 0.65);
    padding-left: 18px;
    position: relative;
    line-height: 1.4;

    &::before {
      content: '';
      position: absolute;
      left: 0;
      top: 6px;
      width: 6px;
      height: 6px;
      border-radius: 50%;
      background: #0f4c81;
    }
  }
}

/* ── RIGHT PANEL ── */
.login-right {
  flex: 1;
  background: #ffffff;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 48px 64px;
  overflow-y: auto;
}

.login-content {
  width: 100%;
  max-width: 400px;
  padding: 8px 0;
}

.login-header {
  margin-bottom: 24px;
}

.login-product {
  display: inline-block;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #0f4c81;
  margin-bottom: 10px;
}

.login-title {
  font-size: 26px;
  font-weight: 700;
  margin: 0 0 6px;
  color: #111827;
  letter-spacing: -0.3px;
}

.login-subtitle {
  margin: 0;
  font-size: 14px;
  color: #6b7280;
}

.form-grid {
  display: grid;
  gap: 14px;
}

.field-divider {
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 4px 0;

  span {
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: #9ca3af;
    white-space: nowrap;
  }

  &::before,
  &::after {
    content: '';
    flex: 1;
    height: 1px;
    background: #e5e7eb;
  }
}

.input-hint {
  display: block;
  font-size: 12px;
  color: #9ca3af;
  margin-top: 4px;

  strong {
    color: #0f4c81;
    font-weight: 600;
  }
}

.consent-field {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  cursor: pointer;
  font-size: 12.5px;
  color: #6b7280;
  line-height: 1.5;

  input[type="checkbox"] {
    margin-top: 3px;
    flex-shrink: 0;
    accent-color: #0f4c81;
    cursor: pointer;
  }

  a {
    color: #0f4c81;
    text-decoration: underline;
  }
}

.login-error {
  font-size: 13px;
  color: #b91c1c;
  margin: 0;
  padding: 10px 12px;
  background: #fef2f2;
  border-radius: 8px;
  border: 1px solid #fecaca;
}

.btn-primary {
  background: #0f4c81;
  color: #ffffff;
  border: none;
  border-radius: 10px;
  padding: 13px 20px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  width: 100%;
  margin-top: 4px;
  transition: background 0.15s ease, transform 0.1s ease;

  &:hover {
    background: #0d3f6e;
  }

  &:active {
    transform: scale(0.99);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
}

.login-links {
  display: flex;
  align-items: center;
  gap: 8px;
  justify-content: center;
}

.link-btn {
  border: none;
  background: transparent;
  padding: 0;
  font-size: 13px;
  color: #6b7280;
  cursor: pointer;
  transition: color 0.15s;

  &:hover {
    color: #0f4c81;
  }
}

/* ── RESPONSIVE ── */
@media (max-width: 900px) {
  .login-shell {
    flex-direction: column;
  }

  .login-left {
    flex: 0 0 auto;
    padding: 28px 24px;
    justify-content: flex-start;
  }

  .brand-mark {
    width: 40px;
    height: 40px;
    font-size: 16px;
    margin-bottom: 12px;
  }

  .brand-name {
    font-size: 22px;
  }

  .brand-tagline {
    font-size: 13px;
    margin-bottom: 0;
  }

  .brand-features {
    display: none;
  }

  .login-right {
    padding: 28px 24px;
    padding-bottom: max(28px, env(safe-area-inset-bottom, 28px));
    align-items: flex-start;
  }
}

@media (max-width: 480px) {
  .login-left {
    padding: 20px;
  }

  .login-right {
    padding: 20px;
    padding-bottom: max(20px, env(safe-area-inset-bottom, 20px));
  }
}
</style>
